import { memo } from "react";
import { Switch } from "@/components/ui/switch";
import { PlansCard } from "@/features/plans/components/PlansCard";
import type { MonitoringType } from "@/constants/monitoring";

/** Card data from SUBSCRIPTION_PLANS (see @/constants/plans) */
export interface ClientAddCardData {
  plan_id: string;
  title: string;
  price: string;
  included: string[];
  popular?: boolean;
}

interface MonitoringTabContentProps {
  monitoringType: MonitoringType;
  cards: ClientAddCardData[];
  disabled: boolean;
  isEditMode?: boolean;
  onDisabledChange: (disabled: boolean) => void;
  selectedPlans: Map<string, { platforms: string[] }>;
  onPlatformChange: (
    planId: string,
    planTitle: string,
    platforms: string[],
    price: string,
    included?: string[],
  ) => void;
}

function MonitoringTabContent({
  monitoringType,
  cards,
  disabled,
  isEditMode = false,
  onDisabledChange,
  selectedPlans,
  onPlatformChange,
}: MonitoringTabContentProps) {
  // In edit mode, only lock plan choice when this monitoring already has a selection (prevent switching tier).
  // If the user just turned the tab from inactive to active, no selection exists yet — allow selecting any plan.
  const tabHasSelection = Array.from(selectedPlans.keys()).some((k) =>
    k.startsWith(`${monitoringType}-`),
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-row items-center gap-2">
        <Switch
          checked={!disabled}
          onCheckedChange={() => onDisabledChange(!disabled)}
        />
        <p className="text-sm leading-none font-semibold tracking-normal text-neutral-800">
          {disabled ? "Inactive" : "Active"}
        </p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] items-end gap-6">
        {cards.map((card) => {
          const key = `${monitoringType}-${card.title}`;
          const plan = selectedPlans.get(key);
          const cardDisabled =
            disabled || (isEditMode && tabHasSelection && !plan);
          return (
            <PlansCard
              key={card.title}
              title={card.title}
              price={card.price}
              theme="dark"
              monitoringType={monitoringType}
              isAdmin={true}
              isPopular={card.popular}
              disabled={cardDisabled}
              selectedPlatforms={plan?.platforms || []}
              onPlatformChange={(platforms) =>
                onPlatformChange(
                  card.plan_id,
                  card.title,
                  platforms,
                  card.price,
                  card.included,
                )
              }
            />
          );
        })}
      </div>
    </div>
  );
}

export default memo(MonitoringTabContent);
